/**
 * @file        utils.js - misc utility functions for dcp protocol implementation
 * @author      Wes Garland, wes@distributive.network
 * @date        Nov 2023
 */
'use strict';

const debugging = require('dcp/debugging').scope('dcp');
const dcpEnv = require('dcp/common/dcp-env');
const { isBrowserPlatform, getenv } = dcpEnv;
const isDebugBuild = require('dcp/common/dcp-build').build === 'debug';
exports.randStrAlphabet = '1234567890abcdefghijklmnopqrstuvwyxzkABCDEFGHIJKLMNOPQRSTUVWXYZ';
exports.randStr21 = require('nanoid').customAlphabet(exports.randStrAlphabet, 21);

if (isDebugBuild && getenv('DCP_DETERMINISTIC_RANDSTR21'))
{
  const hash = require('../common/hash');
  let seq=0;

  /* Generate nonces, ackTokens, etc deterministcally rather than randomly. This is useless from a
   * security POV, but very helpful when trying to compare the output from two different test runs. The
   * "random" string generated depends solely on what order it was generated.
   */
  exports.randStr21 = function deterministicRandStr21()
  {
    const alphabet = exports.randStrAlphabet;
    var id = ++seq + getenv('DCP_DETERMINISTIC_RANDSTR21') + alphabet[seq % alphabet.length];
    id += hash.calculate(hash.eh1, id).slice(4) + alphabet;
    return id.slice(0,21);
  }
}

const utf8Re = /UTF-?8/i;
const haveUnicode = false
      || isBrowserPlatform
      || utf8Re.test(getenv('LANG'))
      || utf8Re.test(getenv('LANGUAGE'))
      || utf8Re.test(getenv('LC_ALL'));

function rpad(str, amt)
{
  if (typeof str !== 'string')
    str = `<${str}>`;
  return str.padEnd(amt, ' ').slice(0,amt);
}

function lpad(str, amt)
{
  if (typeof str !== 'string')
    str = `<${str}>`;
  return str.padStart(amt, ' ').slice(-amt);
}

/**
 * Fancy debug log function to help visualize DCP message flow. Not cheap.
 *
 * @param {string} debugLabel
 * @param {string} sealedMessage    the message we are logging; expected to be in stringified-signed-message format
 * @param {string} dir              'tx' or 'rx'
 * @param {string} [label]          an optional label for the message, default is caller's function name
 */
exports.wireLog = function wireLog(debugLabel, sealedMessage, dir, label)
{
  var id, at, what, arrow;
  try
  {
    const signedMessage = JSON.parse(sealedMessage);
    const body = signedMessage.body;
    const nonce = body?.nonce === undefined ? '' : body?.nonce;

    if (!signedMessage?.body)
      throw new Error('invalid sealed message: ' + sealedMessage);

    if (!label)
    {
      if (dcpEnv.platform !== 'pythonmonkey')
      {
        const callerNameMatches = new Error().stack.split('\n')[2].match(/^\s+at\s+([A-Za-z0-9_$]+)(.*\.([A-Za-z0-9_$]+)\s)?/)
          ?? new Error().stack.split('\n')[2].match(/^(?:async\*)?(?<label>[a-zA-Z0-9_$]+)(?:\/<)?@webpack/);
        if (callerNameMatches)
          label = callerNameMatches[3] || callerNameMatches[1];
      }
      if (!label) /* probably spidermonkey */
        label = new Error().stack.split('\n')[2].replace(/@.*/, '');

      if (!label)
        label = '<unknown caller>';
      else
        label = label.replace(/^.*\$\$/,'');
    }

    switch(dir)
    {
      case 'rx':
      arrow = haveUnicode ? '\u2190' : '<-';
      break;
      case 'tx':
      arrow = haveUnicode ? '\u2192' : '->';
      break;
      default:
      arrow = haveUnicode ? '\ud83d\udca9' : '??';
      break;
    }

    switch (body.type)
    {
      case 'response':
        if (body.success === true)
          what = body.payload && JSON.stringify(body.payload);
        else
        {
          what = 'err:' + body.payload?.name || String(body.payload);
          if (body.payload?.code)
            what += ':' + body.payload.code;
        }
        id = 'id:' + body.id;
        at = 'at:' + body.ackToken;
        break;
      case 'notification':
        what = body.payload?.data.toString();
        id = 'id:' + body.id;
        at = 'at:' + body.ackToken;
        break;
      case 'request':
        what = body.payload?.operation;
        id = 'id:' + body.id;
        at = 'at:' + body.ackToken;
        break;
      case 'batch':
        what = `[batch ${body.payload && body.payload.length}]`;
        id = 'id:' + body.id;
        at = 'at:' + body.ackToken;
        break;
      case 'ack':
        what = body.token;
        id = '';
        at = '';
        break;
      default:
        throw new Error('invalid signed message type ' + body.type);
    }

    const data = body.data ? JSON.stringify(body.data) : '';
    console.debug(debugLabel, rpad(label, 20), arrow, lpad(body.type, 8), rpad(what, 26), rpad('n:' + nonce, 15),
                  id.slice(0,15), (data || '').slice(0,50));
    return;
  }
  catch(error)
  {
    console.error(error);
  }
}

/**
 * Determine if we got the scheduler config from a secure source, eg https or local disk.
 * We assume tha all https transactions have PKI-CA verified.
 *
 * @note protocol::getSchedulerConfigLocation() is populated via node-libs/config.js or dcp-client/index.js
 *
 * @returns true or falsey
 */
function determineIfSecureConfig()
{
  var schedulerConfigLocation = require('dcp/protocol').getSchedulerConfigLocation();
  var schedulerConfigSecure;

  if (schedulerConfigLocation && (schedulerConfigLocation.protocol === 'https:' || schedulerConfigLocation.protocol === 'file:'))
  {
    debugging('strict-mode') && console.debug(`connection: scheduler config location ${schedulerConfigLocation} is secure`); /* from casual eavesdropping */
    schedulerConfigSecure = true;
  }

  if (isDebugBuild)
  {
    debugging('strict-mode') && console.debug('connection: scheduler config location is always secure for debug builds');
    schedulerConfigSecure = 'debug';
  }

  debugging('strict-mode') && console.debug(`connection: Config Location ${schedulerConfigLocation} is ${!schedulerConfigSecure ? 'not secure' : 'secure-' + schedulerConfigSecure}`);
  return schedulerConfigSecure;
}

/**
 * Determine if a URL is secure by examinining the protocol, connection, and information about the
 * process; in particular, we try to determine if the dcp config was securely provided, because if
 * it wasn't, then we can't have a secure location, since the origin could be compromised.
 *
 * "Secure" in this case means "secure against casual eavesdropping", and this information should only
 * be used to refuse to send secrets over the transport or similar.
 *
 * @returns true or falsey
 */
exports.determineIfSecureLocation = function dcp$$utils$determineIfSecureLocation(conn)
{
  var isSecureConfig = determineIfSecureConfig();
  var secureLocation;

  if (!isSecureConfig) /* can't have a secure location without a secure configuration */
    return null;

  if (isDebugBuild || conn.url.protocol === 'https:' || conn.url.protocol === 'tcps:')
    secureLocation = true;
  else if (conn.role.is('initiator') && conn.target.hasOwnProperty('friendLocation') && conn.url === conn.target.friendLocation)
    secureLocation = 'friend';
  else if (conn.options.allowUnencryptedSecrets)
    secureLocation = 'override';
  else
    secureLocation = false;

  debugging('strict-mode') && console.debug(`connection: Location ${conn.url} is ${!secureLocation ? 'not secure' : 'secure-' + secureLocation}`);

  return secureLocation;
}
